import { useMemo, useState } from 'react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip.tsx';
import { cn } from '@/utils/utils.ts';
import {
  getUptimeStatusConfig,
  TUptimeStatusKeys,
  UptimeStatusOptions,
} from '@/components/uptime/uptime-status-config.ts';
import { format } from 'date-fns';

export const UptimeStatusBar = ({
  data,
  align,
  showTime = false,
}: {
  data: { time: string } & {
    [key in TUptimeStatusKeys]: number;
  };
  showTime?: boolean;
  align?: 'start' | 'end' | 'center';
}) => {
  const [tooltipOpen, setTooltipOpen] = useState(false);

  const normalizedData = useMemo(() => {
    const connectedPercent = data[UptimeStatusOptions.Connected] || 0;
    const disconnectedPercent = data[UptimeStatusOptions.Disconnected] || 0;
    let noDataPercent = data[UptimeStatusOptions.NoData] || 0;

    const totalPercentage = connectedPercent + disconnectedPercent + noDataPercent;

    if (totalPercentage < 100) {
      noDataPercent += 100 - totalPercentage;
    } else if (totalPercentage > 100) {
      // percentage values exceed 100%, clamped by noDataPercent = 0
    }

    return {
      Connected: connectedPercent,
      Disconnected: disconnectedPercent,
      NoData: noDataPercent,
    };
  }, [data]);

  const formattedHour = useMemo(() => formatHourToAmPm(data.time), [data.time]);

  const connectedConfig = getUptimeStatusConfig(UptimeStatusOptions.Connected);
  const disconnectedConfig = getUptimeStatusConfig(UptimeStatusOptions.Disconnected);
  const noDataConfig = getUptimeStatusConfig(UptimeStatusOptions.NoData);

  return (
    <div className="relative flex flex-col items-center">
      <TooltipProvider delayDuration={100}>
        <Tooltip open={tooltipOpen}>
          <TooltipTrigger asChild>
            <button
              onClick={() => setTooltipOpen(true)}
              onMouseEnter={() => setTooltipOpen(true)}
              onMouseLeave={() => setTooltipOpen(false)}
              onTouchStart={() => setTooltipOpen(true)}
              className="w-full cursor-pointer"
            >
              <div className="flex h-10 w-3 flex-col overflow-hidden rounded-full">
                {normalizedData.NoData > 0 && (
                  <div
                    className={noDataConfig.backgroundColor}
                    style={{ height: `${normalizedData.NoData}%` }}
                  />
                )}
                {normalizedData.Disconnected > 0 && (
                  <div
                    className={disconnectedConfig.backgroundColor}
                    style={{ height: `${normalizedData.Disconnected}%` }}
                  />
                )}
                {normalizedData.Connected > 0 && (
                  <div
                    className={connectedConfig.backgroundColor}
                    style={{ height: `${normalizedData.Connected}%` }}
                  />
                )}
              </div>
            </button>
          </TooltipTrigger>
          <TooltipContent
            className="border bg-white text-black"
            align={!align ? 'center' : align}
          >
            <div className="flex flex-col gap-2 text-sm">
              <span className="">{formattedHour.long}</span>
              <div className="flex flex-col gap-0">
                <span className={cn('', connectedConfig.textColor)}>
                  {connectedConfig.label}: {normalizedData.Connected} %
                </span>
                <span className={cn('', disconnectedConfig.textColor)}>
                  {disconnectedConfig.label}: {normalizedData.Disconnected} %
                </span>
                <span className={cn('', noDataConfig.textColor)}>
                  {noDataConfig.label}: {normalizedData.NoData} %
                </span>
              </div>
            </div>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>

      {showTime && (
        <div className="absolute -bottom-6 -left-2 w-full text-center text-xs text-gray-500">
          {formattedHour.short}
        </div>
      )}
    </div>
  );
};

const formatHourToAmPm = (hour: string | undefined) => {
  if (!hour)
    return {
      short: '',
      long: '',
    };

  return {
    long: format(hour, 'dd MMM, HH:mm'),
    short: format(hour, 'Haaa'),
  };
};
